/**
 * Ubezpieczenia — teksty obszaru `analytics` (pulpit, szkodowość, ustawienia, demo) pod `ins.analytics.*`. WŁAŚCICIEL: obszar `analytics`.
 * Podpięte już w i18n/insurance.ts — dopisuj klucze tutaj, nie w extra.ts. `en` typowane względem `pl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const insAnalyticsPl = {
  common: {
    demo: 'Dane demo',
    demoHint: 'Część liczb pochodzi z danych demonstracyjnych',
    loadFailed: 'Nie udało się wczytać danych',
    retry: 'Spróbuj ponownie',
    noDriver: 'Kierowca nieustalony',
    noRoute: 'Bez trasy',
    noBranch: 'Bez oddziału',
    noVehicle: 'Bez pojazdu',
    noPremium: 'bez składki',
    days: '{n} dni',
    perVehicleYear: '{value} / pojazd-rok',
    threshold: 'próg {value}',
    vsPrevYear: 'r/r',
    pp: '{value} pp',
    allTypes: 'Wszystkie typy pojazdów',
    allPaths: 'Wszystkie ścieżki',
    clearFilters: 'Wyczyść filtry',
    showAll: 'Pokaż wszystkie',
    collapse: 'Zwiń listę',
    more: '+{n} więcej',
    open: 'Otwórz',
  },
  dash: {
    title: 'Ubezpieczenia i szkody',
    description: 'Polisy, składka, ochrona floty, szkody i przetargi w jednym miejscu',
    hero: {
      policies: 'Polisy ważne',
      premium: 'Składka roczna',
      insured: 'Pojazdy z OC',
      open: 'Otwarte szkody',
      queue: 'Do kwalifikacji',
    },
    actions: {
      policies: 'Polisy',
      analytics: 'Szkodowość',
      import: 'Import polis',
      settings: 'Ustawienia',
    },
    insight: {
      uninsured: 'Pojazdy bez ważnego OC: {count}. Sprawdź, czy polisa jest w drodze, zanim pojazd wyjedzie w trasę.',
      lossRatio: 'Szkodowość 12 mies. {value} przekracza próg krytyczny — przygotuj raport do odnowienia dla brokera.',
      expiring: 'W ciągu {days} dni kończy się ochrona {count} polis — uruchom odnowienie.',
      allGood: 'Każdy aktywny pojazd ma ważne OC, szkodowość poniżej progu.',
      actionUninsured: 'Luki ochrony',
      actionRenewal: 'Raport do odnowienia',
      actionExpiring: 'Kończące się polisy',
    },
    attention: {
      title: 'Wymaga uwagi',
      description: 'Kliknij, aby przejść do listy',
      uninsured: 'Bez ochrony OC',
      expiring: 'Kończą się w {days} dni',
      overdue: 'Terminy po czasie',
      queue: 'Do kwalifikacji',
      none: 'brak',
      morePlates: '+{n} więcej',
    },
    kpi: {
      premium: 'Składka roczna',
      premiumHint: '{policies} polis · umowy flotowe: {contracts}',
      lossRatio: 'Szkodowość 12 mies.',
      lossRatioHint: 'próg {warn} / {critical}',
      lossRatioNoPrev: 'bez porównania r/r (niepełna składka rok wcześniej)',
      claims: 'Szkody 12 mies.',
      claimsHint: 'rok wcześniej: {prev} · {freq}',
      paid: 'Wypłaty ubezpieczycieli 12 mies.',
      paidHint: 'przy składce zarobionej {premium}',
      open: 'Sprawy otwarte',
      openHint: '{overdue} po terminie decyzji',
      openHintNone: 'żadna po terminie decyzji',
      recover: 'Do odzyskania (regres)',
      recoverHint: '{count} spraw z OC sprawców',
      noAmounts: 'kwoty — uprawnienie do szkód',
      trendCaption: 'vs poprzedni miesiąc',
    },
    structure: {
      title: 'Struktura składki',
      description: 'Polisy ważne dziś — ryzyka, ubezpieczyciele i typy pojazdów',
      byLine: 'Ryzyka',
      byInsurer: 'Ubezpieczyciele',
      byType: 'Typy pojazdów',
      policies: 'polisy: {n}',
      empty: 'Brak ważnych polis — wgraj polisy z Excela ubezpieczyciela.',
    },
    trend: {
      title: 'Szkody i wypłaty — 24 miesiące',
      description: 'Liczba szkód wg ścieżki (słupki) i wypłaty ubezpieczycieli (linia) · kliknij miesiąc — lista szkód',
      paid: 'Wypłaty',
      claims: 'Szkody',
      total: '{count} szkód · wypłaty {paid}',
      empty: 'Brak szkód w ostatnich 24 miesiącach',
    },
    funnel: {
      title: 'Szkody otwarte wg etapu',
      description: 'Lejek likwidacji — od kwalifikacji do rozliczenia',
      overdue: '{n} po terminie',
      empty: 'Brak otwartych spraw',
    },
    expiring: {
      title: 'Najbliższe końce polis',
      description: 'Polisy kończące się w {days} dni, jeszcze nieodnowione',
      daysLeft: 'za {n} dni',
      today: 'dziś',
      empty: 'Żadna polisa nie kończy się w najbliższych {days} dniach',
      all: 'Wszystkie kończące się polisy',
    },
    tenders: {
      title: 'Aktywne postępowania',
      description: 'Wybór ubezpieczyciela — przez brokera, zapytanie bezpośrednie albo odnowienie',
      empty: 'Brak trwającego postępowania. Odnowienie warto zacząć ok. 150 dni przed końcem umowy.',
      coverage: 'ochrona od {date}',
      offers: 'Oferty: {n}',
      questions: 'Pytania bez odpowiedzi: {n}',
      next: 'Następny krok',
      best: 'Najlepsza suma ofert {best} vs dziś {current}',
      all: 'Wszystkie postępowania',
    },
    deadlines: {
      title: 'Najbliższe terminy',
      description: 'Końce polis, raty, decyzje ubezpieczycieli, start odnowienia',
      overdue: 'Po terminie',
      overdueCount: '{n} po czasie',
      empty: 'Brak terminów w najbliższym czasie',
      kind: {
        POLICY_END: 'Koniec ochrony',
        RENEWAL_PREP: 'Start odnowienia',
        INSTALLMENT: 'Rata składki',
        DECISION: 'Decyzja ubezpieczyciela',
      },
      policyEnd: 'Koniec ochrony · polisy: {count}',
      renewal: 'Przygotuj raport szkodowości dla brokera przed końcem umowy {date}',
      installmentContract: 'Rata · umowa {ref}',
      installmentPolicies: 'Rata · polisy indywidualne: {count}',
      decision: '{ref} · termin decyzji',
    },
    recent: {
      title: 'Ostatnie szkody',
      all: 'Wszystkie szkody',
      empty: 'Brak szkód w rejestrze',
    },
  },
  an: {
    title: 'Szkodowość',
    description: 'Szkody w stosunku do składki — wg pojazdu, typu, kierowcy, trasy, oddziału, pory i miejsca',
    noPermission: 'Widok wymaga uprawnienia „Szkodowość” (insurance.analytics).',
    hero: {
      lossRatio: 'Szkodowość',
      claims: 'Szkody',
      incurred: 'Poniesione',
      premium: 'Składka zarobiona',
    },
    tabs: { overview: 'Przegląd', when: 'Kiedy', map: 'Mapa', tables: 'Tabele', renewal: 'Do odnowienia' },
    preset: { '12m': '12 mies.', py: 'Rok polisowy', pyprev: 'Poprzedni rok pol.', cy: 'Rok kalendarzowy', custom: 'Własny' },
    from: 'Od',
    to: 'Do',
    range: '{from} – {to}',
    pathNote: 'Przy filtrze ścieżki składka nie jest dzielona — szkodowość pokazuje udział tej ścieżki w całej składce.',
    empty: {
      title: 'Brak danych w wybranym okresie',
      filtered: 'Nie ma szkód ani składki dla tych filtrów. Wyczyść filtry albo rozszerz okres.',
      none: 'W rejestrze nie ma szkód ani składki w tym okresie. Dane demo: pnpm demo:insurance.',
    },
    kpi: {
      lossRatio: 'Szkodowość',
      lossRatioHint: 'składka {premium}',
      frequency: 'Częstość',
      frequencyHint: 'szkody / pojazd-rok · próg {warn}',
      avgClaim: 'Średnia szkoda',
      avgClaimHint: 'z polis (OC, AC, cargo)',
      incurred: 'Poniesione',
      incurredHint: 'wypłaty {paid} + rezerwy {reserve}',
      recover: 'Regresy',
      recoverHint: 'do odzyskania · odzyskano {recovered}',
      uncovered: 'Koszt niepokryty',
      uncoveredHint: 'udziały własne, koszt własny',
      downtime: 'Przestój pojazdów',
      downtimeHint: 'ok. {cost} wg stawek z ustawień',
      cargo: 'Wartość towaru w szkodach',
    },
    charts: {
      byType: 'Szkodowość wg typu pojazdu',
      byTypeDesc: 'Poniesione / składka zarobiona w okresie · linie = progi z ustawień',
      byPath: 'Szkody wg ścieżki',
      byPathDesc: 'Kliknij wycinek — lista szkód',
      trend: 'Trend miesięczny',
      trendDesc: 'Liczba szkód, szkody poniesione i składka zarobiona w miesiącu',
      stages: 'Etapy spraw',
      stagesDesc: 'Wszystkie sprawy z okresu wg etapu',
      claims: 'Szkody',
      incurred: 'Poniesione',
      premium: 'Składka zarobiona',
      lossRatio: 'Szkodowość',
      empty: 'Brak danych do wykresu',
    },
    when: {
      morning: 'szkód w oknie dostaw 5:00–9:00',
      night: 'szkód w nocy 22:00–5:00',
      peakDay: 'najwięcej szkód w tygodniu',
      peakHour: 'najczęstsza godzina: {hour}',
      heatmap: 'Dzień tygodnia × godzina',
      heatmapDesc: 'Czas lokalny zdarzenia · najedź na komórkę, aby zobaczyć liczbę i koszt',
      metricCount: 'Liczba szkód',
      metricAmount: 'Koszt',
      byHour: 'Godziny',
      byWeekday: 'Dni tygodnia',
      season: 'Sezonowość',
      seasonDesc: 'Szkody wg miesiąca roku',
      cell: '{day}, {hour} · {count} szkód · {amount}',
      total: 'Razem',
      empty: 'Brak szkód w wybranym okresie',
      weekdays: { d0: 'Pon', d1: 'Wt', d2: 'Śr', d3: 'Czw', d4: 'Pt', d5: 'Sob', d6: 'Nd' },
      weekdaysLong: { d0: 'poniedziałek', d1: 'wtorek', d2: 'środa', d3: 'czwartek', d4: 'piątek', d5: 'sobota', d6: 'niedziela' },
    },
    map: {
      title: 'Mapa szkód',
      description: 'Koło = szkoda, wielkość wg kwoty, kolor wg ścieżki · kliknij, aby otworzyć sprawę',
      places: 'Najczęstsze miejsca',
      placesDesc: 'Szkody w promieniu ~100 m grupowane razem',
      withoutGeo: '{n} szkód bez współrzędnych — nie ma ich na mapie',
      openClaim: 'Otwórz sprawę',
      empty: 'Brak szkód ze współrzędnymi w tym okresie',
      count: '{n} szkód',
    },
    tables: {
      dims: { vehicle: 'Pojazdy', type: 'Typy pojazdów', driver: 'Kierowcy', route: 'Trasy', branch: 'Oddziały', path: 'Ścieżki' },
      search: 'Szukaj…',
      driversNote: 'Dane kierowców są poufne — tylko do analizy szkodowości.',
      empty: 'Brak pozycji dla tych filtrów',
      col: {
        name: 'Nazwa',
        count: 'Szkody',
        atFault: 'Z winy',
        open: 'Otwarte',
        paid: 'Wypłaty',
        reserve: 'Rezerwy',
        incurred: 'Poniesione',
        toRecover: 'Do odzyskania',
        uncovered: 'Niepokryte',
        premium: 'Składka',
        lossRatio: 'Szkodowość',
        frequency: 'Częstość',
        km: 'Km w okresie',
        per100k: 'Szkody / 100 tys. km',
        downtime: 'Przestój (dni)',
        cargo: 'Towar',
      },
    },
    renewal: {
      title: 'Raport do odnowienia umowy',
      description: 'Podgląd tego, co trafi do Excela dla brokera / ubezpieczyciela',
      download: 'Pobierz Excel',
      demoWarning: 'Raport zawiera dane demonstracyjne — nie przekazuj go ubezpieczycielowi.',
      policyYear: 'Rok polisowy {year}',
      fleet: 'Flota i polisy',
      policies: 'Polisy ważne',
      vehicles: 'Pojazdy',
      premium: 'Składka roczna',
      acSum: 'Suma AC',
      perYear: 'Szkodowość wg roku polisowego',
      perYearDesc: '* rok w toku · † niepełna składka w rejestrze (szkodowość zawyżona)',
      byType: 'Typy pojazdów ({period})',
      causes: 'Najczęstsze przyczyny',
      places: 'Najczęstsze miejsca',
      conclusions: 'Wnioski',
      contracts: 'Umowy flotowe',
      contractRow: '{insurer} · {from} – {to}',
      excelHint: 'Excel: Podsumowanie, Szkody ({claims}), Pojazdy ({vehicles}), Szkodowość wg roku',
      col: { year: 'Rok', claims: 'Szkody', atFault: 'Z winy', paid: 'Wypłaty', reserve: 'Rezerwy', premium: 'Składka', lossRatio: 'Szkodowość' },
      conclusion: {
        ABOVE_THRESHOLD: 'Szkodowość powyżej progu {threshold} w grupach: {groups}. To pierwsze pozycje do omówienia z brokerem.',
        BELOW_THRESHOLD: 'Żaden typ pojazdu nie przekracza progu szkodowości {threshold} w ostatnich trzech latach polisowych.',
        PREMIUM_INCOMPLETE: 'Rejestr nie ma pełnej składki za {years} — szkodowość tych lat jest zawyżona; uzupełnij polisy archiwalne.',
        YOY: 'Szkodowość w roku {cur}: {curPct} wobec {prevPct} w {prev} ({delta}).',
        AT_FAULT_SHARE: 'W ostatnich trzech latach polisowych {count} szkód, z tego {atFault} z winy naszego kierowcy ({pct}%).',
        MORNING_PEAK: '{pct}% szkód zdarza się między 5:00 a 9:00 — w porannym oknie dostaw.',
        NIGHT: '{pct}% szkód w nocy (22:00–5:00) — trasy nocne i załadunek.',
        PEAK_MONTH: 'Najwięcej szkód przypada na {month} ({count}).',
        CARGO_SHARE: 'Szkody w towarze: {count} na łączną wartość ładunku {value} — warto negocjować zakres cargo / OCP.',
        TOP_PLACE: 'Najczęstsze miejsce: {place} ({count} szkód).',
        TO_RECOVER: 'Do odzyskania od sprawców (OC obce, sprawy otwarte): {amount}.',
        NO_CLAIMS: 'Brak szkód w rejestrze dla analizowanego okresu — raport pokazuje sam wykaz floty i polis.',
      },
      months: {
        m1: 'styczeń', m2: 'luty', m3: 'marzec', m4: 'kwiecień', m5: 'maj', m6: 'czerwiec',
        m7: 'lipiec', m8: 'sierpień', m9: 'wrzesień', m10: 'październik', m11: 'listopad', m12: 'grudzień',
      },
      downloadFailed: 'Nie udało się wygenerować raportu',
    },
  },
  settings: {
    title: 'Ustawienia ubezpieczeń',
    description: 'Terminy z OWU i ustawy, kolejka zgłoszeń, stawki przestoju, alerty polis i progi szkodowości',
    save: 'Zapisz zmiany',
    saved: 'Ustawienia zapisane',
    saveFailed: 'Nie udało się zapisać ustawień',
    reset: 'Przywróć domyślne',
    readOnly: 'Podgląd — zmiana ustawień wymaga uprawnienia insurance.manage.',
    customized: 'zmienione',
    default: 'domyślne: {value}',
    lastSaved: 'Ostatni zapis: {date}',
    unsaved: 'Niezapisane zmiany',
    deadlines: {
      title: 'Terminy szkód',
      description: 'Terminy z OWU i ustawy liczone dla każdej sprawy',
      reportDays: 'Zgłoszenie szkody do ubezpieczyciela',
      decisionDays: 'Decyzja ubezpieczyciela (art. 14 u.u.o.)',
      appealDays: 'Odwołanie od decyzji',
      recourseYears: 'Przedawnienie regresu',
      daysSuffix: 'dni',
      yearsSuffix: 'lat',
    },
    queue: {
      title: 'Kolejka „Do kwalifikacji”',
      description: 'Od kiedy i jakie zgłoszenia floty trafiają do kwalifikacji szkód',
      startDate: 'Zgłoszenia od dnia',
      startDateHint: 'Puste = wszystkie zgłoszenia',
      types: 'Typy zgłoszeń floty',
    },
    downtime: {
      title: 'Stawka przestoju pojazdu',
      description: 'Koszt dnia postoju (zł) — koszt pośredni szkody w analizie i regresie',
      suffix: 'zł / dzień',
    },
    alerts: {
      title: 'Alerty końca ochrony',
      description: 'Ile dni przed końcem polisy pokazywać ją na pulpicie i w alertach',
      expiringDays: 'Ostrzegaj przed końcem polisy',
    },
    thresholds: {
      title: 'Progi szkodowości',
      description: 'Kolory na pulpicie, w szkodowości i we wnioskach raportu do odnowienia',
      warn: 'Ostrzeżenie (szkodowość)',
      critical: 'Krytyczny (szkodowość)',
      frequency: 'Częstość szkód — ostrzeżenie',
      frequencySuffix: 'szkód / pojazd-rok',
      orderError: 'Próg ostrzeżenia musi być niższy od krytycznego.',
      preview: 'Podgląd skali',
      good: 'dobrze',
      watch: 'uwaga',
      bad: 'krytycznie',
    },
    demo: {
      title: 'Dane demonstracyjne',
      description: 'Rekordy oznaczone jako demo — do prezentacji, zanim klient dostarczy historię szkód',
      none: 'W module nie ma danych demo.',
      counts: 'Umowy: {contracts} · polisy: {policies} · szkody: {claims} · zgłoszenia: {sources} · postępowania: {tenders}',
      howTo: 'Wczytanie i usunięcie: pnpm --filter @nexops/backend demo:insurance (z --reset — tylko usunięcie). Prawdziwe polisy i szkody zostają.',
    },
  },
};

export const insAnalyticsEn: Shape<typeof insAnalyticsPl> = {
  common: {
    demo: 'Demo data',
    demoHint: 'Some figures come from demo data',
    loadFailed: 'Could not load the data',
    retry: 'Try again',
    noDriver: 'Driver unknown',
    noRoute: 'No route',
    noBranch: 'No branch',
    noVehicle: 'No vehicle',
    noPremium: 'no premium',
    days: '{n} days',
    perVehicleYear: '{value} / vehicle-year',
    threshold: 'threshold {value}',
    vsPrevYear: 'YoY',
    pp: '{value} pp',
    allTypes: 'All vehicle types',
    allPaths: 'All paths',
    clearFilters: 'Clear filters',
    showAll: 'Show all',
    collapse: 'Collapse',
    more: '+{n} more',
    open: 'Open',
  },
  dash: {
    title: 'Insurance & claims',
    description: 'Policies, premium, fleet coverage, claims and tenders in one place',
    hero: { policies: 'Valid policies', premium: 'Annual premium', insured: 'Vehicles with MTPL', open: 'Open claims', queue: 'To qualify' },
    actions: { policies: 'Policies', analytics: 'Loss ratio', import: 'Policy import', settings: 'Settings' },
    insight: {
      uninsured: 'Vehicles without valid MTPL: {count}. Check the policy is on its way before the vehicle goes on a route.',
      lossRatio: 'The 12-month loss ratio {value} is above the critical threshold — prepare the renewal report for the broker.',
      expiring: '{count} policies end within {days} days — start the renewal.',
      allGood: 'Every active vehicle has valid MTPL, loss ratio below threshold.',
      actionUninsured: 'Coverage gaps',
      actionRenewal: 'Renewal report',
      actionExpiring: 'Expiring policies',
    },
    attention: {
      title: 'Needs attention',
      description: 'Click to open the list',
      uninsured: 'Without MTPL',
      expiring: 'Ending within {days} days',
      overdue: 'Overdue deadlines',
      queue: 'To qualify',
      none: 'none',
      morePlates: '+{n} more',
    },
    kpi: {
      premium: 'Annual premium',
      premiumHint: '{policies} policies · fleet contracts: {contracts}',
      lossRatio: 'Loss ratio 12 m',
      lossRatioHint: 'threshold {warn} / {critical}',
      lossRatioNoPrev: 'no YoY comparison (incomplete premium a year earlier)',
      claims: 'Claims 12 m',
      claimsHint: 'a year earlier: {prev} · {freq}',
      paid: 'Insurer payouts 12 m',
      paidHint: 'against earned premium {premium}',
      open: 'Open claims',
      openHint: '{overdue} past the decision deadline',
      openHintNone: 'none past the decision deadline',
      recover: 'To recover (recourse)',
      recoverHint: '{count} third-party claims',
      noAmounts: 'amounts — claims permission',
      trendCaption: 'vs previous month',
    },
    structure: {
      title: 'Premium structure',
      description: 'Policies valid today — risks, insurers and vehicle types',
      byLine: 'Risks',
      byInsurer: 'Insurers',
      byType: 'Vehicle types',
      policies: '{n} policies',
      empty: 'No valid policies — upload the insurer’s Excel.',
    },
    trend: {
      title: 'Claims and payouts — 24 months',
      description: 'Claims by path (bars) and insurer payouts (line) · click a month for the claim list',
      paid: 'Payouts',
      claims: 'Claims',
      total: '{count} claims · payouts {paid}',
      empty: 'No claims in the last 24 months',
    },
    funnel: { title: 'Open claims by stage', description: 'Claims funnel — from qualification to settlement', overdue: '{n} overdue', empty: 'No open claims' },
    expiring: {
      title: 'Nearest policy ends',
      description: 'Policies ending within {days} days, not renewed yet',
      daysLeft: 'in {n} days',
      today: 'today',
      empty: 'No policy ends within the next {days} days',
      all: 'All expiring policies',
    },
    tenders: {
      title: 'Active tenders',
      description: 'Choosing an insurer — via broker, direct request or renewal',
      empty: 'No tender in progress. Start the renewal about 150 days before the contract ends.',
      coverage: 'coverage from {date}',
      offers: 'Offers: {n}',
      questions: 'Unanswered questions: {n}',
      next: 'Next step',
      best: 'Best offers total {best} vs today {current}',
      all: 'All tenders',
    },
    deadlines: {
      title: 'Upcoming deadlines',
      description: 'Policy ends, instalments, insurer decisions, renewal start',
      overdue: 'Overdue',
      overdueCount: '{n} overdue',
      empty: 'No deadlines coming up',
      kind: { POLICY_END: 'Coverage end', RENEWAL_PREP: 'Renewal start', INSTALLMENT: 'Premium instalment', DECISION: 'Insurer decision' },
      policyEnd: 'Coverage end: {count} policies',
      renewal: 'Prepare the loss report for the broker before the contract ends on {date}',
      installmentContract: 'Instalment · contract {ref}',
      installmentPolicies: 'Instalment · {count} individual policies',
      decision: '{ref} · decision deadline',
    },
    recent: { title: 'Latest claims', all: 'All claims', empty: 'No claims in the register' },
  },
  an: {
    title: 'Loss ratio',
    description: 'Claims against premium — by vehicle, type, driver, route, branch, time and place',
    noPermission: 'This view requires the “Loss ratio” permission (insurance.analytics).',
    hero: { lossRatio: 'Loss ratio', claims: 'Claims', incurred: 'Incurred', premium: 'Earned premium' },
    tabs: { overview: 'Overview', when: 'When', map: 'Map', tables: 'Tables', renewal: 'Renewal' },
    preset: { '12m': '12 m', py: 'Policy year', pyprev: 'Previous policy yr', cy: 'Calendar year', custom: 'Custom' },
    from: 'From',
    to: 'To',
    range: '{from} – {to}',
    pathNote: 'With a path filter the premium is not split — the loss ratio shows this path against the whole premium.',
    empty: {
      title: 'No data in the selected period',
      filtered: 'No claims or premium for these filters. Clear the filters or widen the period.',
      none: 'No claims or premium in this period. Demo data: pnpm demo:insurance.',
    },
    kpi: {
      lossRatio: 'Loss ratio',
      lossRatioHint: 'premium {premium}',
      frequency: 'Frequency',
      frequencyHint: 'claims / vehicle-year · threshold {warn}',
      avgClaim: 'Average claim',
      avgClaimHint: 'from policies (MTPL, own damage, cargo)',
      incurred: 'Incurred',
      incurredHint: 'paid {paid} + reserves {reserve}',
      recover: 'Recourse',
      recoverHint: 'to recover · recovered {recovered}',
      uncovered: 'Uncovered cost',
      uncoveredHint: 'own shares, own cost',
      downtime: 'Vehicle downtime',
      downtimeHint: 'approx. {cost} at the settings rates',
      cargo: 'Cargo value in claims',
    },
    charts: {
      byType: 'Loss ratio by vehicle type',
      byTypeDesc: 'Incurred / earned premium in the period · lines = thresholds from settings',
      byPath: 'Claims by path',
      byPathDesc: 'Click a slice — claim list',
      trend: 'Monthly trend',
      trendDesc: 'Claims count, incurred and earned premium per month',
      stages: 'Claim stages',
      stagesDesc: 'All claims of the period by stage',
      claims: 'Claims',
      incurred: 'Incurred',
      premium: 'Earned premium',
      lossRatio: 'Loss ratio',
      empty: 'No data for the chart',
    },
    when: {
      morning: 'of claims in the 5:00–9:00 delivery window',
      night: 'of claims at night 22:00–5:00',
      peakDay: 'most claims in the week',
      peakHour: 'most frequent hour: {hour}',
      heatmap: 'Weekday × hour',
      heatmapDesc: 'Local time of the event · hover a cell for count and cost',
      metricCount: 'Claims count',
      metricAmount: 'Cost',
      byHour: 'Hours',
      byWeekday: 'Weekdays',
      season: 'Seasonality',
      seasonDesc: 'Claims by month of year',
      cell: '{day}, {hour} · {count} claims · {amount}',
      total: 'Total',
      empty: 'No claims in the selected period',
      weekdays: { d0: 'Mon', d1: 'Tue', d2: 'Wed', d3: 'Thu', d4: 'Fri', d5: 'Sat', d6: 'Sun' },
      weekdaysLong: { d0: 'Monday', d1: 'Tuesday', d2: 'Wednesday', d3: 'Thursday', d4: 'Friday', d5: 'Saturday', d6: 'Sunday' },
    },
    map: {
      title: 'Claims map',
      description: 'Circle = claim, size by amount, colour by path · click to open the claim',
      places: 'Most frequent places',
      placesDesc: 'Claims within ~100 m grouped together',
      withoutGeo: '{n} claims without coordinates — not on the map',
      openClaim: 'Open claim',
      empty: 'No claims with coordinates in this period',
      count: '{n} claims',
    },
    tables: {
      dims: { vehicle: 'Vehicles', type: 'Vehicle types', driver: 'Drivers', route: 'Routes', branch: 'Branches', path: 'Paths' },
      search: 'Search…',
      driversNote: 'Driver data is confidential — for loss analysis only.',
      empty: 'No rows for these filters',
      col: {
        name: 'Name',
        count: 'Claims',
        atFault: 'At fault',
        open: 'Open',
        paid: 'Paid',
        reserve: 'Reserves',
        incurred: 'Incurred',
        toRecover: 'To recover',
        uncovered: 'Uncovered',
        premium: 'Premium',
        lossRatio: 'Loss ratio',
        frequency: 'Frequency',
        km: 'Km in period',
        per100k: 'Claims / 100k km',
        downtime: 'Downtime (days)',
        cargo: 'Cargo',
      },
    },
    renewal: {
      title: 'Contract renewal report',
      description: 'Preview of what goes into the Excel for the broker / insurer',
      download: 'Download Excel',
      demoWarning: 'The report contains demo data — do not send it to an insurer.',
      policyYear: 'Policy year {year}',
      fleet: 'Fleet and policies',
      policies: 'Valid policies',
      vehicles: 'Vehicles',
      premium: 'Annual premium',
      acSum: 'Own-damage sum',
      perYear: 'Loss ratio by policy year',
      perYearDesc: '* year in progress · † incomplete premium in the register (loss ratio overstated)',
      byType: 'Vehicle types ({period})',
      causes: 'Most frequent causes',
      places: 'Most frequent places',
      conclusions: 'Conclusions',
      contracts: 'Fleet contracts',
      contractRow: '{insurer} · {from} – {to}',
      excelHint: 'Excel: Summary, Claims ({claims}), Vehicles ({vehicles}), Loss ratio by year',
      col: { year: 'Year', claims: 'Claims', atFault: 'At fault', paid: 'Paid', reserve: 'Reserves', premium: 'Premium', lossRatio: 'Loss ratio' },
      conclusion: {
        ABOVE_THRESHOLD: 'Loss ratio above the {threshold} threshold in: {groups}. These are the first items to discuss with the broker.',
        BELOW_THRESHOLD: 'No vehicle type exceeds the {threshold} loss-ratio threshold in the last three policy years.',
        PREMIUM_INCOMPLETE: 'The register lacks full premium for {years} — those loss ratios are overstated; add archived policies.',
        YOY: 'Loss ratio in {cur}: {curPct} vs {prevPct} in {prev} ({delta}).',
        AT_FAULT_SHARE: 'In the last three policy years {count} claims, {atFault} caused by our driver ({pct}%).',
        MORNING_PEAK: '{pct}% of claims happen between 5:00 and 9:00 — the morning delivery window.',
        NIGHT: '{pct}% of claims at night (22:00–5:00) — night routes and loading.',
        PEAK_MONTH: 'Most claims fall in {month} ({count}).',
        CARGO_SHARE: 'Cargo claims: {count} with total cargo value {value} — worth negotiating cargo / carrier liability cover.',
        TOP_PLACE: 'Most frequent place: {place} ({count} claims).',
        TO_RECOVER: 'To recover from third parties (open third-party claims): {amount}.',
        NO_CLAIMS: 'No claims in the register for the period — the report shows the fleet and policy list only.',
      },
      months: {
        m1: 'January', m2: 'February', m3: 'March', m4: 'April', m5: 'May', m6: 'June',
        m7: 'July', m8: 'August', m9: 'September', m10: 'October', m11: 'November', m12: 'December',
      },
      downloadFailed: 'Could not generate the report',
    },
  },
  settings: {
    title: 'Insurance settings',
    description: 'Policy-terms and statutory deadlines, claims queue, downtime rates, policy alerts and loss-ratio thresholds',
    save: 'Save changes',
    saved: 'Settings saved',
    saveFailed: 'Could not save the settings',
    reset: 'Restore defaults',
    readOnly: 'Read only — changing settings requires insurance.manage.',
    customized: 'changed',
    default: 'default: {value}',
    lastSaved: 'Last saved: {date}',
    unsaved: 'Unsaved changes',
    deadlines: {
      title: 'Claim deadlines',
      description: 'Policy-terms and statutory deadlines applied to every claim',
      reportDays: 'Reporting a claim to the insurer',
      decisionDays: 'Insurer decision (statutory)',
      appealDays: 'Appeal against a decision',
      recourseYears: 'Recourse limitation',
      daysSuffix: 'days',
      yearsSuffix: 'years',
    },
    queue: {
      title: '“To qualify” queue',
      description: 'Since when and which fleet incidents go to claim qualification',
      startDate: 'Incidents from',
      startDateHint: 'Empty = all incidents',
      types: 'Fleet incident types',
    },
    downtime: { title: 'Vehicle downtime rate', description: 'Cost of a day off the road (PLN) — indirect claim cost in analysis and recourse', suffix: 'PLN / day' },
    alerts: { title: 'Coverage end alerts', description: 'How many days before a policy ends to show it on the dashboard and in alerts', expiringDays: 'Warn before the policy ends' },
    thresholds: {
      title: 'Loss-ratio thresholds',
      description: 'Colours on the dashboard, in loss analysis and in the renewal report conclusions',
      warn: 'Warning (loss ratio)',
      critical: 'Critical (loss ratio)',
      frequency: 'Claim frequency — warning',
      frequencySuffix: 'claims / vehicle-year',
      orderError: 'The warning threshold must be lower than the critical one.',
      preview: 'Scale preview',
      good: 'good',
      watch: 'watch',
      bad: 'critical',
    },
    demo: {
      title: 'Demo data',
      description: 'Records flagged as demo — for presentations before the client provides claim history',
      none: 'The module has no demo data.',
      counts: 'Contracts: {contracts} · policies: {policies} · claims: {claims} · incidents: {sources} · tenders: {tenders}',
      howTo: 'Load and remove: pnpm --filter @nexops/backend demo:insurance (with --reset — removal only). Real policies and claims stay.',
    },
  },
};
